import { Card, Grid, Icon, InputLabel, Menu, MenuItem, Select } from "@mui/material";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import React, { memo, useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import DataTable from "components/Tables/DataTable";
import dataEfluente from "./dataEfluente";
import SoftBox from "components/SoftBox";
import { getColor } from "utils/colors";
import SinDatos from "components/Carteles/SinDatos";
import Separador from "../Separador";

const Efluentes = ({ idDeclaracion, listadoEfluentes, setListadoEfluentes, finalizada }) => {
  const [efluente, setEfluente] = useState({});
  const [fileMedicion, setFileMedicion] = useState(null); // Archivo para dispositivo de medición
  const [fileDepuracion, setFileDepuracion] = useState(null); // Archivo para dispositivo de depuración
  const [openMenu, setOpenMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEfluente, setSelectedEfluente] = useState(null);

  const { columns, getRows } = dataEfluente;

  const handleOpenMenu = useCallback((event, item) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(true);
    setSelectedEfluente(item);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
    setOpenMenu(false);
    setSelectedEfluente(null);
  }, []);

  const handleChangeEfluente = (e) => {
    const { name, value } = e.target;
    setEfluente((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (type === "medicion") {
      setFileMedicion(file);
    } else if (type === "depuracion") {
      setFileDepuracion(file);
    }
  };

  const handleAddEfluente = () => {
    const newEfluente = {
      ...efluente,
      fileMedicion: fileMedicion,
      fileDepuracion: fileDepuracion,
    };
    setListadoEfluentes((prev) => [...prev, newEfluente]);
    setEfluente({});
    setFileMedicion(null);
    setFileDepuracion(null);
  };

  const memoizedRows = useMemo(() => {
    if (listadoEfluentes) {
      const listadoSinBorrar = listadoEfluentes.filter((item) => !item.aBorrar);
      return getRows(listadoSinBorrar, handleOpenMenu);
    }
    return [];
  }, [listadoEfluentes, handleOpenMenu]);
  // Render del menú contextual
  const renderMenu = !finalizada && (
    <Menu id="simple-menu" anchorEl={anchorEl} open={Boolean(openMenu)} onClose={handleCloseMenu}>
      <MenuItem
        onClick={() => {
          handleCloseMenu();
          setListadoEfluentes((prev) => {
            if (selectedEfluente.id) {
              return prev.map((item) => {
                if (item.id === selectedEfluente.id) {
                  return { ...item, aBorrar: true };
                }
                return item;
              });
            } else {
              return prev.filter((item) => item !== selectedEfluente);
            }
          });
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <SoftTypography variant="p">Quitar</SoftTypography>
          <Icon fontSize="small" sx={{ verticalAlign: "middle", color: getColor("gobSecondary") }}>
            delete
          </Icon>
        </SoftBox>
      </MenuItem>
    </Menu>
  );
  return (
    <Grid container p={2}>
      <Grid item xs={12}>
        <SoftTypography variant="h4" fontWeight="bold">
          Efluentes Gaseosos
        </SoftTypography>
      </Grid>

      {!finalizada && (
        <Grid item xs={12}>
          <Grid container p={2} spacing={1}>
            <Grid item xs={6} md={6}>
              <InputLabel htmlFor="tipoDeResiduo">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Tipo de Residuo
                </SoftTypography>
              </InputLabel>
              <SoftInput
                id="tipoDeResiduo"
                placeholder="Tipo de Residuo"
                value={efluente.tipoDeResiduo || ""}
                fullWidth
                name="tipoDeResiduo"
                onChange={handleChangeEfluente}
              />
            </Grid>
            <Grid item xs={6} md={6}>
              <InputLabel htmlFor="descripcion">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Descripción
                </SoftTypography>
              </InputLabel>
              <SoftInput
                id="descripcion"
                placeholder="Descripción"
                value={efluente.descripcion || ""}
                fullWidth
                name="descripcion"
                onChange={handleChangeEfluente}
              />
            </Grid>
            <Grid item xs={2} md={2}>
              <InputLabel htmlFor="dispositivoDeMedicion">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Dispositivo de Medición
                </SoftTypography>
              </InputLabel>
              <Select
                id="dispositivoDeMedicion"
                value={efluente.dispositivoDeMedicion || false}
                fullWidth
                name="dispositivoDeMedicion"
                onChange={() => {
                  if (efluente.dispositivoDeMedicion) {
                    setEfluente((prev) => ({
                      ...prev,
                      dispositivoDeMedicion: false,
                      tipoDeDispositivoMedicion: "",
                      frecuenciaDeEmisiones: "",
                      fileMedicion: null,
                    }));
                  } else {
                    setEfluente((prev) => ({ ...prev, dispositivoDeMedicion: true }));
                  }
                }}
                defaultValue={false}
                displayEmpty
                sx={{
                  fontStyle: efluente.dispositivoDeMedicion ? "normal" : "italic",
                }}
              >
                <MenuItem value={true}>Sí</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </Select>
            </Grid>
            {/* {efluente.dispositivoDeMedicion ? ( */}
            <>
              <Grid item xs={3} md={3}>
                <InputLabel htmlFor="tipoDeDispositivoMedicion">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Tipo de Dispositivo de Medición
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  id="tipoDeDispositivoMedicion"
                  placeholder="Tipo de Dispositivo de Medición"
                  value={efluente.tipoDeDispositivoMedicion || ""}
                  fullWidth
                  name="tipoDeDispositivoMedicion"
                  onChange={handleChangeEfluente}
                  disabled={!efluente.dispositivoDeMedicion}
                />
              </Grid>
              <Grid item xs={4} md={4}>
                <InputLabel htmlFor="frecuenciaDeEmisiones">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Frecuencia de Emisiones
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  id="frecuenciaDeEmisiones"
                  placeholder="Frecuencia"
                  value={efluente.frecuenciaDeEmisiones || ""}
                  fullWidth
                  name="frecuenciaDeEmisiones"
                  onChange={handleChangeEfluente}
                  disabled={!efluente.dispositivoDeMedicion}
                />
              </Grid>
              <Grid item xs={3} md={3}>
                <InputLabel htmlFor="fileMedicion">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Resultado de análisis (Medición)
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  id="fileMedicion"
                  type="file"
                  fullWidth
                  onChange={(e) => handleFileChange(e, "medicion")}
                  disabled={!efluente.dispositivoDeMedicion}
                />
              </Grid>
            </>
            {/*  ) : (
               <Grid item xs={10} md={10}></Grid>
             )} */}
            <Grid item xs={2} md={2}>
              <InputLabel htmlFor="dispositivoDeDepuracion">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Dispositivo de Depuración
                </SoftTypography>
              </InputLabel>
              <Select
                id="dispositivoDeDepuracion"
                value={efluente.dispositivoDeDepuracion || false}
                fullWidth
                name="dispositivoDeDepuracion"
                onChange={() => {
                  if (efluente.dispositivoDeDepuracion) {
                    setEfluente((prev) => ({
                      ...prev,
                      dispositivoDeDepuracion: false,
                      tipoDeDispositivoDepuracion: "",
                      fileDepuracion: null,
                    }));
                  } else {
                    setEfluente((prev) => ({ ...prev, dispositivoDeDepuracion: true }));
                  }
                }}
                defaultValue={false}
                displayEmpty
                sx={{
                  fontStyle: efluente.dispositivoDeDepuracion ? "normal" : "italic",
                }}
              >
                <MenuItem value={true}>Sí</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </Select>
            </Grid>
            <>
              <Grid item xs={5} md={5}>
                <InputLabel htmlFor="tipoDeDispositivoDepuracion">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Tipo de Dispositivo de Depuración
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  disabled={!efluente.dispositivoDeDepuracion}
                  id="tipoDeDispositivoDepuracion"
                  placeholder="Tipo de Dispositivo de Depuración"
                  value={efluente.tipoDeDispositivoDepuracion || ""}
                  fullWidth
                  name="tipoDeDispositivoDepuracion"
                  onChange={handleChangeEfluente}
                />
              </Grid>

              <Grid item xs={5} md={5}>
                <InputLabel htmlFor="fileDepuracion">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Resultado de análisis (Depuración)
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  disabled={!efluente.dispositivoDeDepuracion}
                  id="fileDepuracion"
                  type="file"
                  fullWidth
                  onChange={(e) => handleFileChange(e, "depuracion")}
                />
              </Grid>
            </>
            <Grid item xs={8} md={10}>
              <InputLabel htmlFor="observaciones">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Observaciones
                </SoftTypography>
              </InputLabel>
              <SoftInput
                id="observaciones"
                placeholder="Observaciones"
                value={efluente.observaciones || ""}
                fullWidth
                name="observaciones"
                onChange={handleChangeEfluente}
              />
            </Grid>
            <Grid item xs={4} md={2} display={"flex"} alignItems="flex-end">
              <SoftButton
                variant="gradient"
                color="primary"
                fullWidth
                onClick={handleAddEfluente}
                disabled={
                  !efluente.tipoDeResiduo ||
                  !efluente.descripcion ||
                  (efluente.dispositivoDeMedicion &&
                    (!efluente.tipoDeDispositivoMedicion || !fileMedicion)) ||
                  (efluente.dispositivoDeDepuracion &&
                    (!efluente.tipoDeDispositivoDepuracion || !fileDepuracion))
                }
              >
                Agregar +
              </SoftButton>
            </Grid>
          </Grid>
        </Grid>
      )}
      {/* {!finalizada && <Separador />} */}

      <Grid item xs={12} py={1}>
        <Card>
          {listadoEfluentes?.filter((item) => !item.aBorrar).length > 0 ? (
            <DataTable columns={columns} rows={memoizedRows} />
          ) : (
            <SinDatos />
          )}
          {renderMenu}
        </Card>
      </Grid>
    </Grid>
  );
};

export default memo(Efluentes);

Efluentes.propTypes = {
  idDeclaracion: PropTypes.string.isRequired,
  listadoEfluentes: PropTypes.array.isRequired,
  setListadoEfluentes: PropTypes.func.isRequired,
  finalizada: PropTypes.bool,
};
